import {
  AlertCircle,
  ArrowLeft,
  ChevronRight,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  Package,
  ShoppingBag,
  Store,
  TrendingUp,
  Wallet,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { ApiSchemas } from "@/shared/api/schema";
import { ROUTES } from "@/shared/model/routes";
import { useSession } from "@/shared/model/session";
import { Button } from "@/shared/ui/kit/button";
import { Image } from "@/shared/ui/kit/image";
import { Separator } from "@/shared/ui/kit/separator";
import { UnifiedDrawer } from "@/shared/ui/unified-drawer";

import {
  NavUserBalanceMinimal,
  NavStoreBalanceMinimal,
} from "./nav-balance-breakdown";

type NavBarAccountDrawerProps = {
  username: string;
  role: string;
  balance: ApiSchemas["Balance"] | null;
  store?: ApiSchemas["Store"] | null;
  storeBalance?: ApiSchemas["StoreBalance"];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  accountDrawerView: "user" | "store";
  setAccountDrawerView: (view: "user" | "store") => void;
  onTopUp?: () => void;
  onWithdraw?: (availableBalance: number) => void;
};

export const NavBarAccountDrawer = React.memo(function NavBarAccountDrawer({
  username,
  role,
  balance,
  store,
  storeBalance,
  isOpen,
  onOpenChange,
  accountDrawerView,
  setAccountDrawerView,
  onTopUp,
  onWithdraw,
}: NavBarAccountDrawerProps) {
  const { t } = useTranslation();
  const { logout } = useSession();

  const avatarUrl = React.useMemo(
    () => `https://api.dicebear.com/9.x/identicon/svg?seed=${username}`,
    [username],
  );
  const roleLabel = React.useMemo(
    () => (role === "seller" ? t("nav.seller") : t("nav.buyer")),
    [role, t],
  );
  const isSeller = role === "seller";

  const handleClose = React.useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const storeLinks = React.useMemo(
    () => [
      {
        label: t("nav.storeDashboard"),
        route: ROUTES.STORE_DASHBOARD,
        icon: LayoutDashboard,
      },
      { label: t("nav.products"), route: ROUTES.STORE_PRODUCTS, icon: Package },
      { label: t("nav.sales"), route: ROUTES.SALES, icon: TrendingUp },
      { label: t("nav.withdrawals"), route: ROUTES.WITHDRAWALS, icon: Wallet },
      {
        label: t("nav.reviews"),
        route: ROUTES.STORE_REVIEWS,
        icon: MessageSquare,
      },
      {
        label: t("nav.disputes"),
        route: ROUTES.STORE_DISPUTES,
        icon: AlertCircle,
      },
    ],
    [t],
  );

  return (
    <UnifiedDrawer open={isOpen} onOpenChange={onOpenChange} direction="right">
      <UnifiedDrawer.Content className="w-[85vw] max-w-sm h-full flex flex-col">
        {/* Fixed Header */}
        <UnifiedDrawer.Header className="px-4 py-3">
          {accountDrawerView === "user" ? (
            <div className="flex items-center gap-3">
              <Image
                src={avatarUrl}
                alt={`${username} avatar`}
                className="h-8 w-8 rounded-lg bg-muted flex-shrink-0"
              />
              <div className="flex flex-col min-w-0">
                <UnifiedDrawer.Title className="font-medium truncate">
                  {username}
                </UnifiedDrawer.Title>
                <span className="text-xs text-muted-foreground font-normal">
                  {roleLabel}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => setAccountDrawerView("user")}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>

              <div className="flex items-center gap-3 flex-1 min-w-0">
                {store?.avatar_url ? (
                  <Image
                    src={store.avatar_url}
                    alt={store.name}
                    className="h-8 w-8 rounded-lg object-contain"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <Store className="w-5 h-5" />
                  </div>
                )}

                <div className="flex flex-col min-w-0">
                  <UnifiedDrawer.Title className="font-medium truncate">
                    {store?.name}
                  </UnifiedDrawer.Title>
                  <span className="text-xs text-muted-foreground font-normal">
                    {t("nav.store")}
                  </span>
                </div>
              </div>
            </div>
          )}
        </UnifiedDrawer.Header>

        {/* Scrollable Content */}
        <UnifiedDrawer.Body className="overflow-y-auto px-3 py-3">
          {accountDrawerView === "user" ? (
            <>
              {/* User Balance */}
              {balance && (
                <NavUserBalanceMinimal balance={balance} onTopUp={onTopUp} />
              )}
              <Separator className="my-3" />

              {/* User links */}
              <div className="flex flex-col gap-0.5">
                <Link
                  to={ROUTES.DASHBOARD}
                  onClick={handleClose}
                  className="flex items-center gap-3 px-2 py-2 text-sm text-foreground hover:bg-accent rounded-md"
                >
                  <LayoutDashboard className="w-4 h-4 text-muted-foreground" />
                  <span>{t("nav.profile")}</span>
                </Link>
                <Link
                  to={ROUTES.PURCHASES}
                  onClick={handleClose}
                  className="flex items-center gap-3 px-2 py-2 text-sm text-foreground hover:bg-accent rounded-md"
                >
                  <ShoppingBag className="w-4 h-4 text-muted-foreground" />
                  <span>{t("nav.purchases")}</span>
                </Link>
                <Link
                  to={ROUTES.DEPOSITS}
                  onClick={handleClose}
                  className="flex items-center gap-3 px-2 py-2 text-sm text-foreground hover:bg-accent rounded-md"
                >
                  <Wallet className="w-4 h-4 text-muted-foreground" />
                  <span>{t("nav.deposits")}</span>
                </Link>
                <Link
                  to={ROUTES.DISPUTES}
                  onClick={handleClose}
                  className="flex items-center gap-3 px-2 py-2 text-sm text-foreground hover:bg-accent rounded-md"
                >
                  <AlertCircle className="w-4 h-4 text-muted-foreground" />
                  <span>{t("nav.disputes")}</span>
                </Link>
              </div>

              {/* My Store Button - for sellers */}
              {isSeller && store && (
                <>
                  <Separator className="my-3" />
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-3"
                    onClick={() => setAccountDrawerView("store")}
                  >
                    <Store className="w-4 h-4 text-muted-foreground" />
                    <span>{t("nav.myStore")}</span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto" />
                  </Button>
                </>
              )}
            </>
          ) : (
            <>
              {/* Store Balance */}
              {storeBalance && (
                <NavStoreBalanceMinimal
                  storeBalance={storeBalance}
                  onWithdraw={onWithdraw}
                />
              )}
              <Separator className="my-3" />

              {/* Store links */}
              <div className="flex flex-col gap-0.5">
                {storeLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.route}
                      to={link.route}
                      onClick={handleClose}
                      className="flex items-center gap-3 px-2 py-2 text-sm text-foreground hover:bg-accent rounded-md"
                    >
                      <Icon className="w-4 h-4 text-muted-foreground" />
                      <span>{link.label}</span>
                    </Link>
                  );
                })}
              </div>
            </>
          )}
        </UnifiedDrawer.Body>

        {/* Fixed Footer: Logout */}
        <UnifiedDrawer.Footer className="p-4 pt-0 border-t">
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => {
              handleClose();
              logout();
            }}
          >
            <LogOut className="w-4 h-4" />
            {t("nav.logout")}
          </Button>
        </UnifiedDrawer.Footer>
      </UnifiedDrawer.Content>
    </UnifiedDrawer>
  );
});
